<template>
  <!-- Seaded -> Kasutajad uues kujunduses: the new look. Off, the old table. -->
  <list-plate v-if="plate" />
  <loading-content v-else :loading="loading">
    <heading>Kasutajad</heading>
    <list-filter-chips :search="search" :labels="filterLabels" @clear="clearFilter" />
    <v-data-table-server
      :headers="headers"
      :items="users"
      :loading="loading"
      :items-length="count"
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      :items-per-page-options="itemsPerPageOptions"
    >
      <template v-slot:[`body.prepend`]="{ columns }">
        <tr class="d-none d-md-table-row">
          <td>
            <v-text-field v-model="search.firstName" type="text" label="Eesnimi" />
          </td>
          <td>
            <v-text-field v-model="search.lastName" type="text" label="Perenimi" />
          </td>
          <td>
            <v-select v-model="search.role" :items="roleOptions" item-title="title" item-value="id" label="Roll" />
          </td>
          <td :colspan="columns.length - 3"></td>
        </tr>
      </template>
      <template v-slot:[`item.roles`]="{ value }">
        <v-chip v-for="(role, i) in value" :key="i" class="ma-1" color="primary" variant="flat">
          {{role.title}}
        </v-chip>
      </template>
      <template v-slot:[`item.lastLogin`]="{ value }">
        {{formatDate(value)}}
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <v-chip v-if="item.isLocked" color="red" size="x-small" variant="flat">
          {{item.locked ? 'Lukus (püsiv)' : 'Lukus'}}
        </v-chip>
        <span v-else>-</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-btn v-if="item.isLocked" class="mr-2" color="green" @click="unlock(item)" size="x-small">
          Ava lukk
        </v-btn>
        <v-btn color="primary" @click="edit(item)" size="x-small">
          Muuda
        </v-btn>
        <v-btn class="ml-2" color="red" @click="confirmDelete = item.id" size="x-small">
          Kustuta
        </v-btn>
      </template>
    </v-data-table-server>
    <confirm
      @cancel="confirmDelete = null"
      @confirm="destroy()"
      :visible="!!confirmDelete"
      heading="Kustuta kasutaja?"
      text="Ta ei saa enam sisse logida. Tema töökäsud ja märkused jäävad alles."
      action="Kustuta kasutaja"
      :detail="deleteName"
    />
  </loading-content>
</template>

<script>
import api from './../../api'
import dayjs from 'dayjs'
import listMixin from '@/plugins/listMixin'
import { mapState } from 'vuex'
import ListPlate from './ListPlate.vue'

export default {
  components: { ListPlate },
  mixins: [ listMixin ],
  data () {
    return {
      confirmDelete: null,
      listViewConf: {
        apiMethod: api.getUsers,
        itemKey: 'users'
      },
      users: [],
      roles: [],
      search: {
        firstName: this.$route.query.firstName || '',
        lastName: this.$route.query.lastName || '',
        role: this.$route.query.role || '',
        // The Töölaud's "Tehnikute sisselogimised" card opens this page on its
        // own filter. Neither has a control here, so both are shown and cleared
        // as chips above the table.
        //
        // `undefined` rather than the '' the other list pages use, and it
        // matters: listMixin's pickBy strips only undefined, so '' would be
        // sent — and /api/user is the one list endpoint wrapped in validate(),
        // where `active=` fails isInt() and 400s the whole page. The object and
        // calendar routes drop a bad value silently; this one does not.
        active: parseInt(this.$route.query.active) || undefined,
        lastLogin: parseInt(this.$route.query.lastLogin) || undefined
      },
      filterLabels: {
        lastLogin: days => 'Pole loginud üle ' + days + ' päeva',
        active: 'Ainult aktiivsed'
      },
      headers: [{
        title: 'Eesnimi',
        align: 'start',
        value: 'firstName',
        sortable: false
      }, {
        title: 'Perenimi',
        align: 'start',
        value: 'lastName',
        sortable: false
      }, {
        title: 'Rollid',
        align: 'start',
        value: 'roles',
        sortable: false
      }, {
        title: 'Viimane sisselogimine',
        align: 'start',
        value: 'lastLogin',
        sortable: false
      }, {
        title: 'Staatus',
        align: 'start',
        value: 'status',
        sortable: false
      }, {
        title: '',
        align: 'end',
        value: 'actions',
        sortable: false
      }]
    }
  },
  computed: {
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.userPlate)
    },
    deleteName () {
      const user = this.users.find(u => u.id === this.confirmDelete)
      return user ? user.fullName : ''
    },
    roleOptions () {
      return [{
        id: '',
        title: 'Kõik'
      }, ...this.roles.map(role => ({ id: role.title, title: role.title }))]
    }
  },
  methods: {
    // The new look loads its own rows; the old table's fetch would only be
    // thrown away.
    refresh () {
      if (this.plate) return
      return listMixin.methods.refresh.call(this)
    },
    // undefined, not '': see `search` above — an empty string 400s /api/user.
    clearFilter (field) {
      this.search[field] = undefined
    },
    formatDate (date) {
      if (!date) return '-'
      return dayjs(date).format('YYYY-MM-DD HH:mm:ss')
    },
    edit (user) {
      this.$router.push({ name: 'user', params: { userId: user.id }})
    },
    async unlock (user) {
      try {
        this.loading = true
        await api.unlockUser(user.id)
        this.showNotification({ text: 'Konto lukk eemaldatud', color: 'success' })
        this.refresh()
      } catch (err) {
        this.showNotification({ text: (err && err.message) || 'Luku eemaldamine ebaõnnestus', color: 'red' })
      } finally {
        this.loading = false
      }
    },
    async destroy () {
      try {
        this.loading = true
        await api.deleteUser(this.confirmDelete)
        this.confirmDelete = null
        this.showNotification({ text: 'Kasutaja kustutatud', color: 'success' })
        this.refresh()
      } catch (err) {
        this.showNotification({ text: (err && err.message) || 'Kustutamine ebaõnnestus', color: 'red' })
      } finally {
        this.loading = false
      }
    }
  },
  mounted () {
    api.getRoles().then(({ roles }) => this.roles = roles)
  }
}
</script>